/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {createSlice, current, PayloadAction} from '@reduxjs/toolkit';
import {
  SensingSystemArchitectureDesignState, Study, Wizard, Object, Experiment,
  StudyDialog, DataSetDialog, ViewDataSetErrorsDialog
} from './types';
import {
  commonizeGreenLinesForExperiment,
  fetchExperiments, fetchMergedDataSetsForAdasProgram, fetchObjectsForAdasProgram,
  fetchStudies, generateGreenLinesForExperiment,
  smoothGreenLinesForExperiment,
  symmetrizeGreenLinesForExperiment
} from './thunks';
import {DB_KEY_REDLINE_SYNC_STATE} from "./selectors";


const initialState: SensingSystemArchitectureDesignState = {
  studies: [],
  currentStudyId: null,
  experiments: null,
  currentExperimentId: null,
  sensorIdToName: {},
  greenlineIdToProperties: {},
  greenlineIdToPlotData: {},

  greenlineIdToRunState: {},
  sensorIdToBlueLineRunState: {},
  sensorIdToViolationsRunState: {},

  dialogs: {
    studyForm: {
      isOpen: false,
      studyId: null,
      action: null,
    },
    confirmStudyAction: {
      isOpen: false,
      studyId: null,
      action: null,
    },
    downloadDataSet: {
      isOpen: false,
      dataSetId: null,
    },
    viewDataSetErrors: {
      isOpen: false,
      dataSetId: null,
      dataType: null,
    }
  },
  wizards: {
    connectAdasProgram: {
      isOpen: false,
      data: null,
    },
    editSensor: {
      isOpen: false,
      data: null,
    }
  },
  table: {
    isFetching: false,
    isSaving: false,
  },
  loading: {
    fetchExperiments: false,
    generateGreenLines: false,
    commonizeGreenLines: false,
    smoothGreenLines: false,
    symmetrizeGreenLines: false,
  },
}


const sensingSystemArchitectureDesignSlice = createSlice({
  name: 'sensingSystemArchitectureDesign',
  initialState,
  reducers: {
    resetGreenlineAppReduxState: () => initialState,

    setSensorIdToName: (state, action: PayloadAction<any>) => {
      state.sensorIdToName = action.payload
    },

    setGreenlineIdToProperties: (state, action: PayloadAction<any>) => {
      const {data, type} = action.payload
      if (data?.experimentId !== state.experiments?.[0]?.id) {
        return
      }
      switch (type) {
        case 'replace':
          state.greenlineIdToProperties = data.greenlineIdToProperties
          break
        case 'upsert':
          state.greenlineIdToProperties = {...state.greenlineIdToProperties, ...data.greenlineIdToProperties}
          break
        case 'delete':
          const greenlineIds = data.greenlineIds
          for (const greenlineId of greenlineIds) {
            delete state.greenlineIdToProperties[greenlineId]
          }
          break
      }
    },

    setRedlineSyncState: (state, action: PayloadAction<any>) => {
      const data = action.payload
      if (!state.experiments?.[0]?.id || data?.experimentId !== state.experiments?.[0]?.id) {
        return
      }
      const greenlineDatasetId = data.greenlineDatasetId
      const redlineSyncState = data[DB_KEY_REDLINE_SYNC_STATE]
      state.greenlineIdToProperties[greenlineDatasetId][DB_KEY_REDLINE_SYNC_STATE] = redlineSyncState
    },

    setGreenlineIdToPlotData: (state, action: PayloadAction<any>) => {
      const {data, type} = action.payload
      if (data?.experimentId !== state.experiments?.[0]?.id) {
        return
      }
      switch (type) {
        case 'replace':
          state.greenlineIdToPlotData = data.greenlineIdToPlotData
          break
        case 'upsert':
          state.greenlineIdToPlotData = {...state.greenlineIdToPlotData, ...data.greenlineIdToPlotData}
          break
        case 'deleteGreenlineIds':
          const greenlineIds = data.greenlineIds
          for (const greenlineId of greenlineIds) {
            delete state.greenlineIdToPlotData[greenlineId]
          }
          break
        case 'reset':
          state.greenlineIdToPlotData = {}
          break
      }
    },

    setGreenlineIdToRunState: (state, action: PayloadAction<any>) => {
      const {data, type} = action.payload
      if (data?.experimentId !== state.experiments?.[0]?.id) {
        return
      }
      switch (type) {
        case 'replace':
          state.greenlineIdToRunState = data.greenlineIdToRunState
          break
        case 'upsert':
          state.greenlineIdToRunState = {...state.greenlineIdToRunState, ...data.greenlineIdToRunState}
          break
        case 'deleteGreenlineIds':
          const greenlineIds = data.greenlineIds
          for (const greenlineId of greenlineIds) {
            delete state.greenlineIdToRunState[greenlineId]
          }
          break
        case 'reset':
          state.greenlineIdToRunState = {}
          break
      }
    },
    setSensorIdToBluelineRunState: (state, action: PayloadAction<any>) => {
      const {data, type} = action.payload
      if (data?.experimentId !== state.experiments?.[0]?.id) {
        return
      }
      switch (type) {
        case 'replace':
          state.sensorIdToBlueLineRunState = data.sensorIdToBlueLineRunState
          break
        case 'upsert':
          state.sensorIdToBlueLineRunState = {...state.sensorIdToBlueLineRunState, ...data.sensorIdToBlueLineRunState}
          break
        case 'deleteGreenlineIds':
          const greenlineIds = data.greenlineIds
          for (const greenlineId of greenlineIds) {
            delete state.sensorIdToBlueLineRunState[greenlineId]
          }
          break
        case 'reset':
          state.sensorIdToBlueLineRunState = {}
          break
      }
    },
    setSensorIdToViolationsRunState: (state, action: PayloadAction<any>) => {
      const {data, type} = action.payload
      if (data?.experimentId !== state.experiments?.[0]?.id) {
        return
      }
      switch (type) {
        case 'replace':
          state.sensorIdToViolationsRunState = data.sensorIdToViolationsRunState
          break
        case 'upsert':
          state.sensorIdToViolationsRunState = {...state.sensorIdToViolationsRunState, ...data.sensorIdToViolationsRunState}
          break
        case 'deleteGreenlineIds':
          const greenlineIds = data.greenlineIds
          for (const greenlineId of greenlineIds) {
            delete state.sensorIdToViolationsRunState[greenlineId]
          }
          break
        case 'reset':
          state.sensorIdToViolationsRunState = {}
          break
      }
    },

    /** Studies */
    addStudy: (state, action: PayloadAction<Study>) => {
      state.studies.push(action.payload)
    },
    updateStudy: (state, action: PayloadAction<Study>) => {
      const index = state.studies.findIndex(study => study.id === action.payload.id)
      if (index !== -1) {
        state.studies[index] = action.payload
      }
    },
    deleteStudy: (state, action: PayloadAction<string>) => {
      state.studies = state.studies.filter(study => study.id !== action.payload)
    },
    setCurrentStudyId: (state, action: PayloadAction<string | null>) => {
      state.currentStudyId = action.payload
    },
    updateExperiment: (state, action: PayloadAction<Experiment>) => {
      const studyId = action.payload.studyId
      if (state.currentStudyId === studyId && state.experiments) {
        const index = state.experiments.findIndex(experiment => experiment.id === action.payload.id)
        if (index !== -1) {
          state.experiments[index] = action.payload
        }
      }
    },

    /** Study Form dialog */
    openStudyFormDialog: (state, action: PayloadAction<Omit<StudyDialog, 'isOpen'>>) => {
      state.dialogs.studyForm.isOpen = true
      state.dialogs.studyForm.studyId = action.payload.studyId
      state.dialogs.studyForm.action = action.payload.action
    },
    closeStudyFormDialog: (state) => {
      state.dialogs.studyForm.isOpen = false
      state.dialogs.studyForm.studyId = null
      state.dialogs.studyForm.action = null
    },

    /** Study Action Confirmation dialog */
    openStudyActionConfirmationDialog: (state, action: PayloadAction<Omit<StudyDialog, 'isOpen'>>) => {
      state.dialogs.confirmStudyAction.isOpen = true
      state.dialogs.confirmStudyAction.studyId = action.payload.studyId
      state.dialogs.confirmStudyAction.action = action.payload.action
    },
    closeStudyActionConfirmationDialog: (state) => {
      state.dialogs.confirmStudyAction.isOpen = false
      state.dialogs.confirmStudyAction.studyId = null
      state.dialogs.confirmStudyAction.action = null
    },

    /** Connect ADAS Program wizard */
    openConnectAdasProgramWizard: (state, action: PayloadAction<Omit<Wizard, 'isOpen'>>) => {
      state.wizards.connectAdasProgram.isOpen = true
      state.wizards.connectAdasProgram.data = action.payload.data
    },
    closeConnectAdasProgramWizard: (state) => {
      state.wizards.connectAdasProgram.isOpen = false
      state.wizards.connectAdasProgram.data = null
    },
    setConnectAdasProgramWizardData: (state, action: PayloadAction<Wizard['data']>) => {
      state.wizards.connectAdasProgram.data = action.payload
    },

    /** Edit Sensor wizard */
    openEditSensorWizard: (state, action: PayloadAction<Omit<Wizard, 'isOpen'>>) => {
      state.wizards.editSensor.isOpen = true
      state.wizards.editSensor.data = action.payload.data
    },
    closeEditSensorWizard: (state) => {
      state.wizards.editSensor.isOpen = false
      state.wizards.editSensor.data = null
    },
    setEditSensorWizardData: (state, action: PayloadAction<Wizard['data']>) => {
      state.wizards.editSensor.data = action.payload
    },
    setEditSensorWizardSelectedDataSet: (state, action: PayloadAction<string[]>) => {
      if (!state.wizards.editSensor.data) {
        state.wizards.editSensor.data = {}
      }
      if (!state.wizards.editSensor.data.dataSets) {
        state.wizards.editSensor.data.dataSets = {}
      }
      state.wizards.editSensor.data.dataSets.selectedDataSet = action.payload
    },
    setEditSensorWizardObjectsData: (state, action: PayloadAction<any>) => {
      if(!state.wizards.editSensor.data) {
        state.wizards.editSensor.data = {}
      }
      if (!state.wizards.editSensor.data.objects) {
        state.wizards.editSensor.data.objects = {}
      }
      const {type, data} = action.payload

      if (type === 'addOrOverride') {
        state.wizards.editSensor.data.objects.data = {...state.wizards.editSensor.data.objects.data, ...data}
        return
      } else if(type === 'replace') {
        state.wizards.editSensor.data.objects.data = data
      } else if (type === 'onlyAddNew') {
        state.wizards.editSensor.data.objects.data = {...data, ...state.wizards.editSensor.data.objects.data}
      } else if (type === 'reset') {
        state.wizards.editSensor.data.objects.data = {}
      }
    },
    setEditSensorWizardSelectedObjectIds: (state, action: PayloadAction<any>) => {
      if(!state.wizards.editSensor.data) {
        state.wizards.editSensor.data = {}
      }
      if (!state.wizards.editSensor.data.objects) {
        state.wizards.editSensor.data.objects = {}
      }
      state.wizards.editSensor.data.objects.selectedObjectIds = action.payload
    },

    /** Table loading state */
    setTableIsFetching: (state, action: PayloadAction<boolean>) => {
      state.table.isFetching = action.payload
    },
    setTableIsSaving: (state, action: PayloadAction<boolean>) => {
      state.table.isSaving = action.payload
    },

    /** Download data set dialog */
    openDownloadDataSetDialog: (state, action: PayloadAction<Omit<DataSetDialog, 'isOpen'>>) => {
      state.dialogs.downloadDataSet.isOpen = true
      state.dialogs.downloadDataSet.dataSetId = action.payload.dataSetId
    },
    closeDownloadDataSetDialog: (state) => {
      state.dialogs.downloadDataSet.isOpen = false
      state.dialogs.downloadDataSet.dataSetId = null
    },

    /** View data set errors dialog */
    openViewDataSetErrorsDialog: (state, action: PayloadAction<Omit<ViewDataSetErrorsDialog, 'isOpen'>>) => {
      state.dialogs.viewDataSetErrors.dataType = action.payload.dataType
      state.dialogs.viewDataSetErrors.isOpen = true
      state.dialogs.viewDataSetErrors.dataSetId = action.payload.dataSetId
    },
    closeViewDataSetErrorsDialog: (state) => {
      state.dialogs.viewDataSetErrors.isOpen = false
      state.dialogs.viewDataSetErrors.dataSetId = null
    },
  },

  /**
   * Extra reducers for handling async actions
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudies.pending, (state) => {
        state.table.isFetching = true
      })
      .addCase(fetchStudies.fulfilled, (state, action: PayloadAction<Study[]>) => {
        state.studies = action.payload
        state.table.isFetching = false
      })
      .addCase(fetchStudies.rejected, (state, action) => {
        console.error(action.error.message)
        state.table.isFetching = false
      })

      .addCase(fetchExperiments.pending, (state) => {
        state.loading.fetchExperiments = true
      })
      .addCase(fetchExperiments.fulfilled, (state, action: PayloadAction<Experiment[]>) => {
        state.experiments = action.payload
        state.loading.fetchExperiments = false
      })
      .addCase(fetchExperiments.rejected, (state, action) => {
        console.error(action.error.message)
        state.loading.fetchExperiments = false
      })

      .addCase(fetchObjectsForAdasProgram.pending, (state) => {
        if(!state.wizards.editSensor.data) {
          state.wizards.editSensor.data = {}
        }
        if (!state.wizards.editSensor.data.objects) {
          state.wizards.editSensor.data.objects = {}
        }
        state.wizards.editSensor.data.objects.isFetching = true
      })
      .addCase(fetchObjectsForAdasProgram.fulfilled, (state, action: PayloadAction<Object[]>) => {
        if(!state.wizards.editSensor.data) {
          state.wizards.editSensor.data = {}
        }
        if (!state.wizards.editSensor.data.objects) {
          state.wizards.editSensor.data.objects = {}
        }
        state.wizards.editSensor.data.objects.data = action.payload
        state.wizards.editSensor.data.objects.isFetching = false
      })
      .addCase(fetchObjectsForAdasProgram.rejected, (state, action) => {
        console.error(action.error.message)
        if(!state.wizards.editSensor.data) {
          state.wizards.editSensor.data = {}
        }
        if (!state.wizards.editSensor.data.objects) {
          state.wizards.editSensor.data.objects = {}
        }
        state.wizards.editSensor.data.objects.isFetching = false
      })

      .addCase(fetchMergedDataSetsForAdasProgram.pending, (state) => {
        if(!state.wizards.editSensor.data) {
          state.wizards.editSensor.data = {}
        }
        if (!state.wizards.editSensor.data.dataSets) {
          state.wizards.editSensor.data.dataSets = {}
        }
        state.wizards.editSensor.data.dataSets.isFetching = true
      })
      .addCase(fetchMergedDataSetsForAdasProgram.fulfilled, (state, action: PayloadAction<any>) => {
        if(!state.wizards.editSensor.data) {
          state.wizards.editSensor.data = {}
        }
        if (!state.wizards.editSensor.data.dataSets) {
          state.wizards.editSensor.data.dataSets = {}
        }
        state.wizards.editSensor.data.dataSets.data = action.payload
        state.wizards.editSensor.data.dataSets.isFetching = false
      })
      .addCase(fetchMergedDataSetsForAdasProgram.rejected, (state, action) => {
        console.error(action.error.message)
        if(!state.wizards.editSensor.data) {
          state.wizards.editSensor.data = {}
        }
        if (!state.wizards.editSensor.data.dataSets) {
          state.wizards.editSensor.data.dataSets = {}
        }
        state.wizards.editSensor.data.dataSets.isFetching = false
      })

      .addCase(generateGreenLinesForExperiment.pending, (state) => {
        state.loading.generateGreenLines = true
      })
      .addCase(generateGreenLinesForExperiment.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading.generateGreenLines = false
      })
      .addCase(generateGreenLinesForExperiment.rejected, (state, action) => {
        console.error(action.error.message)
        state.loading.generateGreenLines = false
      })

      .addCase(commonizeGreenLinesForExperiment.pending, (state) => {
        state.loading.commonizeGreenLines = true
      })
      .addCase(commonizeGreenLinesForExperiment.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading.commonizeGreenLines = false
      })
      .addCase(commonizeGreenLinesForExperiment.rejected, (state, action) => {
        console.error(action.error.message)
        state.loading.commonizeGreenLines = false
      })

      .addCase(smoothGreenLinesForExperiment.pending, (state) => {
        state.loading.smoothGreenLines = true
      })
      .addCase(smoothGreenLinesForExperiment.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading.smoothGreenLines = false
      })
      .addCase(smoothGreenLinesForExperiment.rejected, (state, action) => {
        console.error(action.error.message)
        state.loading.smoothGreenLines = false
      })

      .addCase(symmetrizeGreenLinesForExperiment.pending, (state) => {
        state.loading.symmetrizeGreenLines = true
      })
      .addCase(symmetrizeGreenLinesForExperiment.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading.symmetrizeGreenLines = false
      })
      .addCase(symmetrizeGreenLinesForExperiment.rejected, (state, action) => {
        console.error(action.error.message)
        state.loading.symmetrizeGreenLines = false
      })
  },
})

export const {
  resetGreenlineAppReduxState,

  setSensorIdToName,

  setGreenlineIdToProperties, setRedlineSyncState, setGreenlineIdToRunState, setSensorIdToBluelineRunState, setSensorIdToViolationsRunState, setGreenlineIdToPlotData,

  addStudy, updateStudy, deleteStudy, setCurrentStudyId, updateExperiment,
  openStudyFormDialog, closeStudyFormDialog,
  openStudyActionConfirmationDialog, closeStudyActionConfirmationDialog,
  openConnectAdasProgramWizard, closeConnectAdasProgramWizard,
  setConnectAdasProgramWizardData,
  openEditSensorWizard, closeEditSensorWizard,
  setEditSensorWizardData, setEditSensorWizardSelectedDataSet, setEditSensorWizardObjectsData,
  setEditSensorWizardSelectedObjectIds,
  openDownloadDataSetDialog, closeDownloadDataSetDialog,
  openViewDataSetErrorsDialog, closeViewDataSetErrorsDialog,
  setTableIsFetching, setTableIsSaving,
} = sensingSystemArchitectureDesignSlice.actions

export const { actions, reducer } = sensingSystemArchitectureDesignSlice
